import React, { useState, useEffect, useCallback } from 'react';
import { sourcesApi, schedulerApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Heartbeat, Clock, CheckCircle, XCircle, WarningCircle, ArrowsClockwise,
  CaretRight, Database, Timer, Plugs, ShieldCheck, Spinner, Play,
  CalendarBlank, ChartBar, ArrowLeft, Scales, Lightning
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const FreshnessBadge = ({ freshness }) => {
  const styles = {
    FRESH: 'text-emerald-400 bg-emerald-500/15 border-emerald-500/30',
    DEGRADED: 'text-amber-400 bg-amber-500/15 border-amber-500/30',
    STALE: 'text-red-400 bg-red-500/15 border-red-500/30',
  };
  const icons = { FRESH: CheckCircle, DEGRADED: WarningCircle, STALE: XCircle };
  const Icon = icons[freshness] || WarningCircle;
  return <span data-testid={`freshness-${freshness}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-medium ${styles[freshness] || 'text-zinc-400 bg-zinc-500/15 border-zinc-500/30'}`}><Icon size={12} weight="fill" />{freshness}</span>;
};

const StatusBadge = ({ status }) => {
  const styles = { SUCCEEDED: 'text-emerald-400 bg-emerald-500/15 border-emerald-500/30', FAILED: 'text-red-400 bg-red-500/15 border-red-500/30', PARTIAL: 'text-amber-400 bg-amber-500/15 border-amber-500/30', RUNNING: 'text-blue-400 bg-blue-500/15 border-blue-500/30' };
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-medium ${styles[status] || 'text-zinc-400 bg-zinc-500/15 border-zinc-500/30'}`}>{status}</span>;
};

const MetricCard = ({ label, value, unit, color, icon: Icon, tooltip }) => (
  <div className="p-3 bg-zinc-800/40 rounded-lg" title={tooltip || undefined}>
    <p className="text-xs text-zinc-500 mb-1 flex items-center gap-1">
      {Icon && <Icon size={12} />}
      <span>{label}</span>
      {tooltip && <span className="text-[9px] text-zinc-600 ml-0.5">ⓘ</span>}
    </p>
    <p className={`text-lg font-mono font-bold ${color || 'text-white'}`}>{value}{unit && <span className="text-xs text-zinc-500 ml-1">{unit}</span>}</p>
  </div>
);

const SourceHealthPage = () => {
  const [sources, setSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [presets, setPresets] = useState({});
  const [loading, setLoading] = useState(true);
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [backfillDates, setBackfillDates] = useState({ from: '', to: '' });
  const [backfillResult, setBackfillResult] = useState(null);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [expandedRun, setExpandedRun] = useState(null);

  const fetchSources = useCallback(async () => {
    setLoading(true);
    try {
      const [srcRes, schedRes] = await Promise.all([
        sourcesApi.list(), schedulerApi.list()
      ]);
      setSources(srcRes.data || []);
      setSchedules(schedRes.data?.schedules || []);
      setPresets(schedRes.data?.presets || {});
    } catch (err) { toast.error('Failed to load source health data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSources(); }, [fetchSources]);

  const handleSelectSource = async (source) => {
    setSelectedSource(source);
    setHealthData(null);
    setBackfillResult(null);
    try {
      const res = await sourcesApi.getHealthTimeline(source.id, 30);
      setHealthData(res.data);
    } catch (err) { toast.error('Failed to load health data'); }
  };

  const handleScheduleChange = async (sourceId, field, value) => {
    const existing = schedules.find(s => s.source_id === sourceId) || { source_id: sourceId, enabled: false, interval_key: 'daily' };
    const updated = { ...existing, [field]: value };
    try {
      await schedulerApi.upsert(sourceId, updated);
      toast.success('Schedule updated');
      fetchSources();
    } catch (err) { toast.error('Failed to update schedule'); }
  };

  const handleBackfill = async () => {
    if (!selectedSource || !backfillDates.from) return;
    setBackfillLoading(true);
    setBackfillResult(null);
    try {
      const res = await sourcesApi.backfillReplay(selectedSource.id, {
        date_from: backfillDates.from,
        date_to: backfillDates.to || backfillDates.from,
        use_mock: true,
      });
      setBackfillResult(res.data);
      toast.success(`Backfill ${res.data.status}: ${res.data.new?.records || 0} records`);
      // Refresh health data
      const healthRes = await sourcesApi.getHealthTimeline(selectedSource.id, 30);
      setHealthData(healthRes.data);
    } catch (err) { toast.error('Backfill failed'); }
    finally { setBackfillLoading(false); }
  };

  const getSchedule = (sourceId) => schedules.find(s => s.source_id === sourceId);

  // Source list view
  if (!selectedSource) {
    return (
      <div className="space-y-6" data-testid="source-health-page">
        <div>
          <h1 className="text-2xl font-medium font-['Outfit'] tracking-tight flex items-center gap-3"><Heartbeat size={28} className="text-emerald-400" weight="duotone" />Source Health</h1>
          <p className="text-sm text-zinc-400 mt-1">Sync history, freshness, completeness, and scheduling</p>
        </div>

        {loading && <div className="text-center py-12 text-zinc-500"><Spinner size={24} className="animate-spin mx-auto mb-2" />Loading sources...</div>}

        <div className="space-y-3">
          {sources.map(src => {
            const sched = getSchedule(src.id);
            return (
              <div key={src.id} data-testid={`source-card-${src.id}`} className="bg-[#121214] border border-white/5 rounded-lg p-5 cursor-pointer hover:border-white/10 transition-colors" onClick={() => handleSelectSource(src)}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <Plugs size={20} className="text-blue-400" />
                    <div>
                      <p className="text-sm font-medium">{src.display_name}</p>
                      <p className="text-xs text-zinc-500">{src.source_type} · {src.outlet_name || src.outlet_id}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`status-badge ${src.status === 'CONNECTED' ? 'status-connected' : src.status === 'SYNCING' ? 'status-syncing' : 'status-degraded'}`}>{src.status}</span>
                    <CaretRight size={14} className="text-zinc-500" />
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs text-zinc-500">
                  {sched?.enabled && <span className="flex items-center gap-1 text-emerald-400"><Timer size={12} />{presets[sched.interval_key]?.label || sched.interval_key}</span>}
                  {sched?.last_run_at && <span className="flex items-center gap-1"><Clock size={12} />Last: {new Date(sched.last_run_at).toLocaleString()}</span>}
                  {sched?.last_run_status && <StatusBadge status={sched.last_run_status} />}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Source detail view with health timeline
  return (
    <div className="space-y-6" data-testid="source-health-detail">
      {/* Back + Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => { setSelectedSource(null); setHealthData(null); }}><ArrowLeft size={16} /></Button>
        <div>
          <h1 className="text-xl font-medium flex items-center gap-2"><Heartbeat size={24} className="text-emerald-400" />{selectedSource.display_name}</h1>
          <p className="text-xs text-zinc-500">{selectedSource.source_type} · {selectedSource.outlet_name || selectedSource.outlet_id}</p>
        </div>
      </div>

      {!healthData ? (
        <div className="text-center py-12 text-zinc-500"><Spinner size={24} className="animate-spin mx-auto" /></div>
      ) : (
        <>
          {/* Health Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <div className="p-3 bg-zinc-800/40 rounded-lg">
              <p className="text-xs text-zinc-500 mb-1">Freshness</p>
              <FreshnessBadge freshness={healthData.freshness} />
              {healthData.staleness_hours != null && <p className="text-xs text-zinc-500 mt-1">{healthData.staleness_hours}h ago</p>}
            </div>
            <MetricCard label="Success Rate" value={healthData.sync_success_rate} unit="%" color={healthData.sync_success_rate >= 90 ? 'text-emerald-400' : 'text-amber-400'} icon={CheckCircle} />
            <MetricCard
              label="Data Quality"
              value={healthData.data_quality_percent ?? '—'}
              unit={healthData.data_quality_percent != null ? '%' : ''}
              color={healthData.data_quality_percent == null ? 'text-zinc-500' : healthData.data_quality_percent >= 95 ? 'text-emerald-400' : healthData.data_quality_percent >= 80 ? 'text-amber-400' : 'text-red-400'}
              icon={Database}
              tooltip="% of canonical rows that passed ingestion validation and normalization checks (no negative discounts, no missing timestamps, no missing item names, no invariant violations)."
            />
            <MetricCard label="Validation Failures" value={healthData.validation_failure_rate} unit="%" color={healthData.validation_failure_rate < 5 ? 'text-emerald-400' : 'text-red-400'} icon={ShieldCheck} />
            <MetricCard label="Pagination" value={healthData.pagination_completeness} unit="%" color="text-cyan-400" icon={ChartBar} />
            <MetricCard label="Records Ingested" value={healthData.total_records_ingested?.toLocaleString()} color="text-white" icon={Database} />
          </div>

          {/* Last sync info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="p-4 bg-emerald-500/5 rounded-lg border border-emerald-500/20">
              <p className="text-xs text-emerald-400 mb-1 flex items-center gap-1"><CheckCircle size={12} />Last Successful Sync</p>
              <p className="text-sm font-mono">{healthData.last_successful_sync ? new Date(healthData.last_successful_sync).toLocaleString() : 'Never'}</p>
            </div>
            <div className="p-4 bg-red-500/5 rounded-lg border border-red-500/20">
              <p className="text-xs text-red-400 mb-1 flex items-center gap-1"><XCircle size={12} />Last Failed Sync</p>
              <p className="text-sm font-mono">{healthData.last_failed_sync ? new Date(healthData.last_failed_sync).toLocaleString() : 'None'}</p>
              {healthData.last_error && <p className="text-xs text-zinc-500 mt-1 truncate">{healthData.last_error}</p>}
            </div>
          </div>

          {/* Scheduler */}
          <div className="bg-[#121214] border border-white/5 rounded-lg p-5" data-testid="scheduler-section">
            <h3 className="text-sm font-medium mb-3 flex items-center gap-2"><Timer size={16} className="text-blue-400" />Sync Schedule</h3>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Label className="text-xs text-zinc-500">Enabled</Label>
                <Switch checked={healthData.schedule?.enabled || false} onCheckedChange={(v) => handleScheduleChange(selectedSource.id, 'enabled', v)} data-testid="schedule-toggle" />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-zinc-500">Interval</Label>
                <Select value={healthData.schedule?.interval_key || 'daily'} onValueChange={(v) => handleScheduleChange(selectedSource.id, 'interval_key', v)}>
                  <SelectTrigger className="w-40 bg-[#18181B] border-white/10 text-sm" data-testid="schedule-interval"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(presets).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {healthData.schedule?.next_run_at && (
                <div className="text-xs text-zinc-500 flex items-center gap-1"><Clock size={12} />Next: {new Date(healthData.schedule.next_run_at).toLocaleString()}</div>
              )}
              {healthData.schedule?.last_run_at && (
                <div className="text-xs text-zinc-500 flex items-center gap-1">Last: {new Date(healthData.schedule.last_run_at).toLocaleString()} <StatusBadge status={healthData.schedule.last_run_status} /></div>
              )}
            </div>
          </div>

          {/* Action bar */}
          <div className="flex items-center gap-3">
            <Button onClick={() => setBackfillOpen(true)} variant="outline" className="border-orange-500/30 text-orange-400 hover:bg-orange-500/10 text-sm" data-testid="backfill-btn">
              <ArrowsClockwise size={14} className="mr-1.5" />Backfill / Replay
            </Button>
          </div>

          {/* Sync Runs Timeline */}
          <div className="bg-[#121214] border border-white/5 rounded-lg p-5" data-testid="sync-timeline">
            <h3 className="text-sm font-medium mb-3 flex items-center gap-2"><Clock size={16} className="text-cyan-400" />Sync History ({healthData.sync_runs?.length || 0} runs)</h3>
            {healthData.sync_runs?.length > 0 ? (
              <div className="space-y-2">
                {healthData.sync_runs.map((run, i) => (
                  <div key={run.id || i} className="bg-zinc-800/30 rounded-lg overflow-hidden border border-white/5">
                    <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-zinc-800/50 transition-colors" onClick={() => setExpandedRun(expandedRun === i ? null : i)}>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-zinc-600 font-mono w-16">{run.run_type}</span>
                        <StatusBadge status={run.status} />
                        <span className="text-xs text-zinc-400">{run.records_ingested || 0} records</span>
                        {run.confidence && <span className="text-xs text-zinc-500 font-mono">{run.confidence}%</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-zinc-600">{run.started_at ? new Date(run.started_at).toLocaleString() : '—'}</span>
                        {(run.validation_summary || run.reconciliation_summary) && <CaretRight size={14} className={`text-zinc-500 transition-transform ${expandedRun === i ? 'rotate-90' : ''}`} />}
                      </div>
                    </div>
                    {expandedRun === i && (
                      <div className="px-3 pb-3 space-y-2">
                        {run.error_summary && <div className="text-xs text-red-400 p-2 bg-red-500/5 rounded">{run.error_summary}</div>}
                        {run.validation_summary && (
                          <div className="text-xs p-2 bg-zinc-800/50 rounded">
                            <span className="text-zinc-500">Validation:</span>
                            <span className="text-emerald-400 ml-2">{run.validation_summary.passed} PASS</span>
                            <span className="text-amber-400 ml-2">{run.validation_summary.warned} WARN</span>
                            <span className="text-red-400 ml-2">{run.validation_summary.failed} FAIL</span>
                          </div>
                        )}
                        {run.reconciliation_summary && (
                          <div className="text-xs p-2 bg-zinc-800/50 rounded">
                            <span className="text-zinc-500">Reconciliation:</span>
                            <span className={`ml-2 ${run.reconciliation_summary.match ? 'text-emerald-400' : 'text-amber-400'}`}>
                              {run.reconciliation_summary.match ? 'MATCH' : `MISMATCH ${run.reconciliation_summary.variance_percent?.toFixed(1)}%`}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-zinc-500 text-sm">No sync runs recorded yet</div>
            )}
          </div>
        </>
      )}

      {/* Backfill Dialog */}
      <Dialog open={backfillOpen} onOpenChange={setBackfillOpen}>
        <DialogContent className="bg-[#0e0e10] border-white/5" data-testid="backfill-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ArrowsClockwise size={20} className="text-orange-400" />Backfill / Replay</DialogTitle>
            <DialogDescription className="text-zinc-500">Re-sync a date range. Existing records will be replaced (idempotent).</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">From Date</Label>
                <Input type="date" value={backfillDates.from} onChange={e => setBackfillDates(d => ({ ...d, from: e.target.value }))} className="bg-[#18181B] border-white/10 text-sm" data-testid="backfill-from" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">To Date</Label>
                <Input type="date" value={backfillDates.to} onChange={e => setBackfillDates(d => ({ ...d, to: e.target.value }))} className="bg-[#18181B] border-white/10 text-sm" data-testid="backfill-to" />
              </div>
            </div>

            <div className="p-3 bg-orange-500/10 rounded-lg border border-orange-500/20 text-xs text-orange-300">
              This will delete existing records in the date range and re-sync from source. No duplicates. Previous vs new delta will be shown.
            </div>

            <Button onClick={handleBackfill} disabled={backfillLoading || !backfillDates.from} className="w-full bg-orange-600 hover:bg-orange-700" data-testid="backfill-run-btn">
              {backfillLoading ? <Spinner size={14} className="mr-2 animate-spin" /> : <Play size={14} className="mr-2" />}
              Run Backfill
            </Button>

            {backfillResult && (
              <div className="space-y-3 mt-4">
                <div className="flex items-center gap-2"><StatusBadge status={backfillResult.status} /><span className="text-xs text-zinc-400">{backfillResult.date_range?.from} to {backfillResult.date_range?.to}</span></div>

                {/* Delta */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="p-3 bg-zinc-800/40 rounded-lg text-center">
                    <p className="text-xs text-zinc-500">Previous</p>
                    <p className="text-lg font-mono font-bold">{backfillResult.previous?.records || 0}</p>
                    <p className="text-xs text-zinc-600">{backfillResult.previous?.net_amount?.toLocaleString()}</p>
                  </div>
                  <div className="p-3 bg-emerald-500/5 rounded-lg text-center border border-emerald-500/20">
                    <p className="text-xs text-zinc-500">New</p>
                    <p className="text-lg font-mono font-bold text-emerald-400">{backfillResult.new?.records || 0}</p>
                    <p className="text-xs text-zinc-600">{backfillResult.new?.net_amount?.toLocaleString()}</p>
                  </div>
                  <div className="p-3 bg-cyan-500/5 rounded-lg text-center border border-cyan-500/20">
                    <p className="text-xs text-zinc-500">Replaced</p>
                    <p className="text-lg font-mono font-bold text-cyan-400">{backfillResult.delta?.records_replaced || 0}</p>
                    <p className="text-xs text-zinc-600">{backfillResult.delta?.net_change > 0 ? '+' : ''}{backfillResult.delta?.net_change?.toLocaleString()}</p>
                  </div>
                </div>

                {/* Validation */}
                {backfillResult.validation && (
                  <div className="p-3 bg-zinc-800/30 rounded-lg text-xs">
                    <p className="text-zinc-500 mb-1">Validation</p>
                    <span className="text-emerald-400">{backfillResult.validation.passed} PASS</span>
                    <span className="text-amber-400 ml-3">{backfillResult.validation.warned} WARN</span>
                    <span className="text-red-400 ml-3">{backfillResult.validation.failed} FAIL</span>
                  </div>
                )}

                {/* Reconciliation */}
                {backfillResult.reconciliation && (
                  <div className="p-3 bg-zinc-800/30 rounded-lg text-xs">
                    <p className="text-zinc-500 mb-1">Reconciliation</p>
                    <span className={backfillResult.reconciliation.match ? 'text-emerald-400' : 'text-amber-400'}>
                      {backfillResult.reconciliation.match ? 'MATCH' : `MISMATCH — ${backfillResult.reconciliation.variance_percent?.toFixed(1)}% variance`}
                    </span>
                  </div>
                )}

                {backfillResult.confidence && (
                  <div className="text-xs text-zinc-500">Confidence: <span className="font-mono">{backfillResult.confidence}%</span></div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SourceHealthPage;
